import { MATCHUP_BAND } from '@/utils/desktopColumn';

export interface SquareData {
  /** Square ID (0-99) */
  id: number;
  /** Row index (0-9) */
  row: number;
  /** Column index (0-9) */
  col: number;
  /** Is this square selected by current user */
  isSelected?: boolean;
  /** Is this square owned by another user */
  isOwned?: boolean;
  /** Owner display name (if owned) */
  ownerName?: string;
  /** Is this a winning square */
  isWinner?: boolean;
  /** Row number (0-9, assigned at game lock) */
  rowNumber?: number;
  /** Column number (0-9, assigned at game lock) */
  colNumber?: number;
  /** Percentage value (0.00-1.00) for this square */
  percentage?: number;
  /** Heatmap tier (1-5) for percentage display: 1=highest, 5=lowest. Used for opacity grading. */
  percentageTier?: number;
}

export interface TeamLabelInfo {
  /** Team name */
  name: string;
  /** Team primary color (hex) */
  primaryColor: string;
  /** Team text color (hex) */
  textColor?: string;
}

export interface SquareOptionData {
  square_number: number;
  entry_count: number;
  win_probability: number;
}

export interface SquaresGridProps {
  /** Team 1 (left/rows) */
  team1: TeamLabelInfo;
  /** Team 2 (top/columns) */
  team2: TeamLabelInfo;
  /** Grid squares data (100 squares) */
  squares?: SquareData[];
  /** Are numbers assigned yet (game locked) */
  numbersAssigned?: boolean;
  /** Row numbers (0-9 in random order, shown when assigned) */
  rowNumbers?: number[];
  /** Column numbers (0-9 in random order, shown when assigned) */
  colNumbers?: number[];
  /** Currently selected squares */
  selectedSquares?: number[];
  /** Square click handler */
  onSquareClick?: (squareId: number) => void;
  /** Is the grid locked (no more selections) */
  isLocked?: boolean;
  /** Show results mode (percentages and numbers revealed) */
  showResults?: boolean;
  /** Options data with entry counts and win probabilities */
  optionsData?: Map<number, SquareOptionData>; // Maps square number (1-100) to option data
  /** Is the game live (started) - win probability only shown when live */
  isGameLive?: boolean;
  /** Winning squares by period (Q1, Q2, Q3, FINAL or HT, FINAL for NCAA -> square_number) */
  winningSquaresByPeriod?: Record<string, number>;
  /** Contest periods config - used to detect NCAA (HT + FINAL) vs NBA (Q1–Q4) for display labels */
  periods?: Array<{ periodCode?: string; period_code?: string }>;
  /** Contest sport (e.g. NBA, NCAAMB) — NBA winning badges use HT / FT instead of Q2 / Q4 */
  sport?: string;
}

const TEAM_BAR_BORDER = '#000000';

/**
 * Row-label geometry must be derived from the grid, never from the viewport.
 *
 * The 10x10 grid is a `flex-1` track of `aspect-square` cells, so its height
 * follows whatever width the board is given. The two row-label strips beside it
 * (the team-1 bar and the row-number gutter) used to size each row with
 * `calc((100vw - 64px) / 10)`, which only coincides with a real square while the
 * column *is* the viewport, i.e. on mobile. Inside the desktop column the labels
 * ran multiples longer than the grid they annotate.
 *
 * Both strips now mirror the grid's own track definition — ten equal rows and the
 * same 1px gap — so a label is always exactly as tall as the square beside it, at
 * any column width.
 */
const GRID_LINE_COUNT = 10;
const GRID_GAP = '1px';
const GRID_ROW_TRACKS = `repeat(${GRID_LINE_COUNT}, minmax(0, 1fr))`;

/**
 * Per-cell type, keyed to the grid rather than the window.
 *
 * These were `1.8vw` / `2.2vw`, which tracked a square only while the grid filled
 * the viewport. `cqw` is a percentage of the grid container (which declares
 * `container-type: inline-size`), so a cell is always ~1/10th of it. The old
 * mobile sizes are reproduced: at a 375px viewport the grid is ~311px wide, where
 * 1.8vw = 6.75px ≈ 2.2cqw and 2.2vw = 8.25px ≈ 2.7cqw.
 */
const WINNER_BADGE_FONT_SIZE = 'clamp(6px, 2.2cqw, 8px)';
const SQUARE_PERCENT_FONT_SIZE = 'clamp(7px, 2.7cqw, 10px)';

/** Heatmap opacity by tier: 1=highest % (100%), 5=lowest % (40%) */
const PERCENTAGE_TIER_OPACITY: Record<number, number> = {
  1: 1,
  2: 0.85,
  3: 0.7,
  4: 0.55,
  5: 0.4,
};

/**
 * Darken a hex color by a given amount (0-1).
 * Returns a hex string with reduced lightness.
 */
function darkenColor(hex: string, amount: number): string {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  const darken = (c: number) => Math.max(0, Math.round(c - c * amount));
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return `#${toHex(darken(r))}${toHex(darken(g))}${toHex(darken(b))}`;
}

/**
 * Build a linear gradient for team colour bars (darker palette).
 * Background: dark base to slightly brighter dark (e.g. #731E27 → #AE1F23 style).
 * Works for any team primary color.
 */
function teamGradient(primaryColor: string, direction = 'to right'): string {
  const dark = darkenColor(primaryColor, 0.55);
  const midDark = darkenColor(primaryColor, 0.35);
  return `linear-gradient(${direction}, ${dark} 0%, ${midDark} 50%, ${dark} 100%)`;
}

/**
 * Squares Grid Component
 * 10x10 grid with team labels on sides
 */
export default function SquaresGrid({
  team1,
  team2,
  squares,
  numbersAssigned = false,
  rowNumbers,
  colNumbers,
  selectedSquares = [],
  onSquareClick,
  isLocked = false,
  showResults = false,
  optionsData,
  isGameLive = false,
  winningSquaresByPeriod = {},
  periods,
  sport,
}: SquaresGridProps) {
  // NCAA games use 2 halves (HT, FINAL) -> badge HT / FT; NBA uses Q1–Q3 + FINAL -> badge Q1 / HT / Q3 / FT
  const periodCodes = Array.isArray(periods)
    ? periods.map(p => String(p?.periodCode ?? p?.period_code ?? '').toUpperCase()).filter(Boolean)
    : [];
  const isNCAA =
    periodCodes.length === 2 &&
    periodCodes.includes('HT') &&
    (periodCodes.includes('FINAL') || periodCodes.includes('FT'));
  const isNBA = (sport ?? '').trim().toUpperCase() === 'NBA';
  // Generate default squares if not provided
  let gridSquares =
    squares ||
    Array.from({ length: 100 }, (_, i) => ({
      id: i,
      row: Math.floor(i / 10),
      col: i % 10,
    }));

  // Calculate percentages and add win probability data
  if (numbersAssigned && rowNumbers && colNumbers) {
    gridSquares = gridSquares.map(square => {
      const rowNum = rowNumbers[square.row];
      const colNum = colNumbers[square.col];
      const squareNumber = square.id + 1; // Square numbers are 1-100

      // Get win probability from options data if available
      let percentage: number | undefined;
      if (optionsData) {
        const optionData = optionsData.get(squareNumber);
        if (optionData) {
          percentage = optionData.win_probability;
        }
      }

      // In results mode, show percentage if available
      if (showResults && percentage !== undefined) {
        return {
          ...square,
          rowNumber: rowNum,
          colNumber: colNum,
          percentage,
        };
      } else if (showResults) {
        // Fallback: calculate from entry counts if available
        const optionData = optionsData?.get(squareNumber);
        const winProb = optionData?.win_probability || 0;
        return {
          ...square,
          rowNumber: rowNum,
          colNumber: colNum,
          percentage: winProb,
        };
      } else {
        // Not in results mode, just add row/col numbers
        return {
          ...square,
          rowNumber: rowNum,
          colNumber: colNum,
        };
      }
    });

    // Value-based tiers: actual percentage determines tier (not rank). Same % = same tier. 0% → tier 5, max % → tier 1.
    if (showResults) {
      const withPercentage = gridSquares.filter(s => s.percentage !== undefined);
      if (withPercentage.length > 0) {
        const values = withPercentage.map(s => s.percentage ?? 0);
        const minPct = Math.min(...values);
        const maxPct = Math.max(...values);
        gridSquares = gridSquares.map(sq => {
          const p = sq.percentage;
          if (p === undefined) return sq;
          let tier: number;
          if (minPct === maxPct) {
            tier = 5; // All same % (e.g. all 0%) → tier 5 (lowest opacity)
          } else {
            // Map [min, max] → [tier 5, tier 1]. Higher % = lower tier number = higher opacity.
            const t = 5 - (4 * (p - minPct)) / (maxPct - minPct);
            tier = Math.max(1, Math.min(5, Math.round(t)));
          }
          return { ...sq, percentageTier: tier };
        });
      }
    }
  }

  const handleSquareClick = (squareId: number) => {
    // Allow clicks in results mode or when not locked
    if ((showResults || !isLocked) && onSquareClick) {
      onSquareClick(squareId);
    }
  };

  return (
    <div className={`w-full ${MATCHUP_BAND}`}>
      {/* Grid Container */}
      <div className="flex">
        {/* VS Label + Team 1 Label (Left Side) */}
        <div className="flex flex-col w-[40px] flex-shrink-0">
          {/* VS corner - uses vs-ui.svg asset */}
          <div className="h-[36px] flex items-center justify-center relative">
            <img
              src="/vs-ui.svg"
              alt=""
              className="absolute inset-0 w-full h-full"
              aria-hidden="true"
            />
            <span className="relative z-10 text-zinc-400 text-[10px] font-semibold">VS</span>
          </div>

          {/* Spacing between VS and team bar */}
          <div className="h-[3px] flex-shrink-0" />

          {/* Team 1 vertical label or Row Numbers */}
          {showResults && numbersAssigned && rowNumbers ? (
            <div
              className="flex-1 min-h-0 rounded-[4px] overflow-hidden"
              style={{
                background: teamGradient(team1.primaryColor, 'to bottom'),
                border: `1px solid ${TEAM_BAR_BORDER}`,
                display: 'grid',
                gridTemplateRows: GRID_ROW_TRACKS,
                gap: GRID_GAP,
              }}
            >
              {rowNumbers.map((num, idx) => (
                <div key={`row-label-${idx}`} className="min-h-0 flex items-center justify-center">
                  <span
                    className="font-bold text-sm tracking-wider text-white"
                    style={{
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                      transform: 'rotate(180deg)',
                    }}
                  >
                    {num}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="flex-1 flex items-center justify-center rounded-[4px] overflow-hidden"
              style={{
                background: teamGradient(team1.primaryColor, 'to bottom'),
                border: `1px solid ${TEAM_BAR_BORDER}`,
              }}
            >
              <span
                className="font-bold text-sm tracking-wider"
                style={{
                  fontFamily: '"Saira Extra Condensed", sans-serif',
                  color: team1.textColor || '#ffffff',
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                }}
              >
                {team1.name.toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Main Grid Area */}
        <div className="flex-1 min-w-0">
          {/* Team 2 Header (Top) or Column Numbers */}
          {showResults && numbersAssigned && colNumbers ? (
            <div className="flex flex-col">
              <div
                className="flex h-[36px] rounded-[4px] overflow-hidden ml-[3px]"
                style={{
                  background: teamGradient(team2.primaryColor, 'to right'),
                  border: `1px solid ${TEAM_BAR_BORDER}`,
                }}
              >
                {colNumbers.map((num, idx) => (
                  <div key={`col-label-${idx}`} className="flex-1 flex items-center justify-center">
                    <span
                      className="font-bold text-sm tracking-wider text-white"
                      style={{ color: team2.textColor || '#ffffff' }}
                    >
                      {num}
                    </span>
                  </div>
                ))}
              </div>
              {/* Spacing between team bar and grid */}
              <div className="h-[3px] flex-shrink-0" />
            </div>
          ) : (
            <>
              <div
                className="h-[36px] flex items-center justify-center rounded-[4px] overflow-hidden ml-[3px]"
                style={{
                  background: teamGradient(team2.primaryColor, 'to right'),
                  border: `1px solid ${TEAM_BAR_BORDER}`,
                }}
              >
                <span
                  className="font-bold text-sm tracking-wider"
                  style={{
                    fontFamily: '"Saira Extra Condensed", sans-serif',
                    color: team2.textColor || '#ffffff',
                  }}
                >
                  {team2.name.toUpperCase()}
                </span>
              </div>

              {/* Numbers Row (if assigned but not in results mode) */}
              {numbersAssigned && colNumbers && (
                <div className="flex h-[24px] bg-[#1a1a1a]">
                  {colNumbers.map((num, idx) => (
                    <div
                      key={`col-${idx}`}
                      className="flex-1 flex items-center justify-center text-zinc-400 text-xs font-mono"
                    >
                      {num}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Grid Rows */}
          <div className="flex">
            {/* Row Numbers (if assigned but not in results mode - numbers are in team label area) */}
            {numbersAssigned && rowNumbers && !showResults && (
              <div
                className="w-[24px] bg-[#1a1a1a]"
                style={{
                  display: 'grid',
                  gridTemplateRows: GRID_ROW_TRACKS,
                  gap: GRID_GAP,
                }}
              >
                {rowNumbers.map((num, idx) => (
                  <div
                    key={`row-${idx}`}
                    className="min-h-0 flex items-center justify-center text-zinc-400 text-xs font-mono"
                  >
                    {num}
                  </div>
                ))}
              </div>
            )}

            {/* 10x10 Grid */}
            <div
              className="flex-1 min-w-0 grid grid-cols-10 gap-[1px] bg-[#2a2a2a]"
              /* Size container so per-cell type scales with the grid, not the window. */
              style={{ containerType: 'inline-size' }}
            >
              {gridSquares.map(square => {
                const isSelected = selectedSquares.includes(square.id);
                const squareNumber = square.id + 1; // Square numbers are 1-100
                const optionData = optionsData?.get(squareNumber);

                // Determine which period badges this square won (display label + position + periodIndex for reveal order)
                type Badge = { label: string; position: 0 | 1 | 2 | 3; periodIndex: number };
                const wonPeriodBadges: Badge[] = [];
                const periodMap =
                  winningSquaresByPeriod && typeof winningSquaresByPeriod === 'object'
                    ? winningSquaresByPeriod
                    : {};
                // Animation order: periodCodes is already Q1,Q2,Q3,FT or HT,FT from API
                const periodOrderIndex = (periodUpper: string) => {
                  const idx = periodCodes.findIndex(
                    c => c === periodUpper || (c === 'FT' && periodUpper === 'FINAL')
                  );
                  return idx >= 0 ? idx : 0;
                };
                Object.entries(periodMap).forEach(([period, winningSquareNumber]) => {
                  if (winningSquareNumber !== squareNumber) return;
                  const periodUpper = String(period || '').toUpperCase();
                  const periodIndex = periodOrderIndex(periodUpper);
                  if (isNCAA) {
                    if (periodUpper === 'HT') {
                      wonPeriodBadges.push({ label: 'HT', position: 0, periodIndex });
                    } else if (periodUpper === 'FINAL' || periodUpper === 'FT') {
                      wonPeriodBadges.push({ label: 'FT', position: 3, periodIndex });
                    }
                  } else if (isNBA) {
                    // NBA: halftime prize aligns with end of Q2 → show "HT"; final period → "FT" (not Q2/Q4)
                    if (periodUpper === 'FINAL' || periodUpper === 'FT' || periodUpper === 'Q4') {
                      wonPeriodBadges.push({ label: 'FT', position: 3, periodIndex });
                    } else if (periodUpper === 'Q1') {
                      wonPeriodBadges.push({ label: 'Q1', position: 0, periodIndex });
                    } else if (periodUpper === 'Q2') {
                      wonPeriodBadges.push({ label: 'HT', position: 1, periodIndex });
                    } else if (periodUpper === 'Q3') {
                      wonPeriodBadges.push({ label: 'Q3', position: 2, periodIndex });
                    } else if (periodUpper.startsWith('Q')) {
                      const pos = parseInt(periodUpper.replace('Q', ''), 10);
                      if (pos === 1) {
                        wonPeriodBadges.push({ label: 'Q1', position: 0, periodIndex });
                      } else if (pos === 2) {
                        wonPeriodBadges.push({ label: 'HT', position: 1, periodIndex });
                      } else if (pos === 3) {
                        wonPeriodBadges.push({ label: 'Q3', position: 2, periodIndex });
                      } else if (pos === 4) {
                        wonPeriodBadges.push({ label: 'FT', position: 3, periodIndex });
                      }
                    } else {
                      wonPeriodBadges.push({ label: period, position: 0, periodIndex });
                    }
                  } else {
                    if (periodUpper === 'FINAL' || periodUpper === 'FT') {
                      wonPeriodBadges.push({ label: 'Q4', position: 3, periodIndex });
                    } else if (periodUpper === 'Q1') {
                      wonPeriodBadges.push({ label: 'Q1', position: 0, periodIndex });
                    } else if (periodUpper === 'Q2') {
                      wonPeriodBadges.push({ label: 'Q2', position: 1, periodIndex });
                    } else if (periodUpper === 'Q3') {
                      wonPeriodBadges.push({ label: 'Q3', position: 2, periodIndex });
                    } else if (periodUpper.startsWith('Q')) {
                      const pos = parseInt(periodUpper.replace('Q', ''), 10);
                      if (pos >= 1 && pos <= 4) {
                        wonPeriodBadges.push({
                          label: periodUpper,
                          position: (pos - 1) as 0 | 1 | 2 | 3,
                          periodIndex,
                        });
                      }
                    } else {
                      wonPeriodBadges.push({ label: period, position: 0, periodIndex });
                    }
                  }
                });
                // One period at a time: each period’s squares finish their full outline before the next starts
                const OUTLINE_DURATION_S = 1.2;
                const outlineRevealDelay =
                  wonPeriodBadges.length > 0
                    ? Math.min(...wonPeriodBadges.map(b => b.periodIndex)) * OUTLINE_DURATION_S
                    : 0;

                return (
                  <SquareCell
                    key={square.id}
                    square={square}
                    isSelected={isSelected}
                    isLocked={isLocked}
                    showResults={showResults}
                    onClick={() => handleSquareClick(square.id)}
                    winProbability={isGameLive ? optionData?.win_probability : undefined}
                    entryCount={isGameLive ? optionData?.entry_count : undefined}
                    totalEntries={
                      isGameLive && optionsData
                        ? Array.from(optionsData.values()).reduce(
                            (sum, opt) => sum + opt.entry_count,
                            0
                          )
                        : undefined
                    }
                    wonPeriodBadges={wonPeriodBadges}
                    outlineRevealDelay={outlineRevealDelay}
                    outlineDurationS={OUTLINE_DURATION_S}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Individual Square Cell
 */
/** Position for period badge: 0=top-left, 1=top-right, 2=bottom-left, 3=bottom-right */
type BadgePosition = 0 | 1 | 2 | 3;

/**
 * Where a period badge sits, per corner.
 *
 * The corners of `Green-Rounded-Highlight.svg` sit at 4.31/38 ≈ 11.3% from each
 * edge of a square cell. Cells are `aspect-square` at every breakpoint, so the
 * same inset pins the badge to the artwork on desktop as on mobile.
 *
 * ⚠️ Whole, literal class strings — Tailwind never sees a class assembled at runtime.
 */
const WINNER_BADGE_POSITION: Record<BadgePosition, string> = {
  0: 'top-[11.3%] left-[11.3%]',
  1: 'top-[11.3%] right-[11.3%]',
  2: 'bottom-[11.3%] left-[11.3%]',
  3: 'bottom-[11.3%] right-[11.3%]',
};

function SquareCell({
  square,
  isSelected,
  isLocked,
  showResults = false,
  onClick,
  winProbability,
  entryCount,
  totalEntries,
  wonPeriodBadges = [],
  outlineRevealDelay = 0,
  outlineDurationS = 1.2,
}: {
  square: SquareData;
  isSelected: boolean;
  isLocked: boolean;
  showResults?: boolean;
  onClick: () => void;
  winProbability?: number;
  entryCount?: number;
  totalEntries?: number;
  /** Winning period badges: label (e.g. Q1, H1, FT) and corner position */
  wonPeriodBadges?: Array<{ label: string; position: BadgePosition }>;
  /** Delay in seconds before outline reveal (Q1→0, Q2→1.2s, Q3→2.4s, Q4→3.6s so each period runs after the previous finishes) */
  outlineRevealDelay?: number;
  /** Duration of the outline animation (used for badge delay) */
  outlineDurationS?: number;
}) {
  const isOwned = square.isOwned;

  // In results mode, allow clicking selected squares
  const isClickable = showResults ? isSelected : !isLocked && !isOwned;

  // Calculate win probability percentage for display
  const winProbPercent =
    winProbability !== undefined
      ? (winProbability * 100).toFixed(1)
      : entryCount !== undefined && totalEntries !== undefined && totalEntries > 0
        ? ((entryCount / totalEntries) * 100).toFixed(1)
        : null;

  /* Pulsing glow only when selected in play view (before submit), not in results view.
   * Glow is applied to a rounded wrapper so it follows the rounded square contours. */
  const showSelectionGlow = isSelected && !showResults;
  /* Match the asset's own `rx="4"` in a 30x30 viewBox → ~13%. */
  const squareRoundness = 'rounded-[13%]';

  return (
    <button
      onClick={onClick}
      disabled={!isClickable}
      className={`
        aspect-square flex items-center justify-center transition-all relative p-[2px]
        ${isOwned && !isSelected ? 'bg-[#1a1a1a]' : ''}
        ${!isOwned && !isSelected && wonPeriodBadges.length === 0 ? 'bg-[#1a1a1a] hover:bg-[#252525]' : ''}
        ${isClickable ? 'cursor-pointer' : 'cursor-default'}
        ${showResults && isSelected ? 'hover:opacity-90' : ''}
      `}
      aria-label={`Square ${square.row + 1}-${square.col + 1}${winProbPercent ? `, ${winProbPercent}% win probability` : ''}`}
      title={
        winProbPercent
          ? `${winProbPercent}% win probability${entryCount !== undefined ? ` (${entryCount} ${entryCount === 1 ? 'entry' : 'entries'})` : ''}`
          : undefined
      }
    >
      {/* Rounded glow wrapper: same shape as square asset so glow/pulse follows rounded contours, tight to square */}
      {showSelectionGlow && (
        <div
          className={`pointer-events-none absolute inset-[2px] ${squareRoundness} animate-selection-glow`}
          aria-hidden
        />
      )}
      {/* Selection overlay: solid white inner border, rounded to match asset, tight to square */}
      {showSelectionGlow && (
        <div
          className={`pointer-events-none absolute inset-[2px] ${squareRoundness}`}
          style={{
            border: '2px solid rgba(255, 255, 255, 0.98)',
          }}
        />
      )}
      {/* Winning square: (1) Animated draw SVG plays, (2) then we show the static highlight */}
      {wonPeriodBadges.length > 0 && (
        <div className="absolute inset-0 pointer-events-none z-20">
          {/* Phase 1: Animated draw – runs for 1.2s then hides so the static asset shows */}
          <svg
            className="absolute inset-0 w-full h-full winner-outline-hide-after-reveal"
            viewBox="0 0 32 32"
            fill="none"
            preserveAspectRatio="none"
            style={{
              animationDelay: `${outlineRevealDelay}s`,
            }}
          >
            <rect
              x="1"
              y="1"
              width="30"
              height="30"
              rx="4.27"
              ry="4.27"
              stroke="#53D337"
              strokeWidth="2.5"
              className="winner-outline-reveal-rounded"
              style={{ animationDelay: `${outlineRevealDelay}s` }}
            />
          </svg>
          {/* Phase 2: Static Green-Rounded-Highlight asset – fades in after the draw completes */}
          <img
            src="/Green-Rounded-Highlight.svg"
            alt=""
            className="absolute inset-0 w-full h-full object-contain pointer-events-none winner-asset-show"
            style={{
              animationDelay: `${outlineRevealDelay + outlineDurationS}s`,
            }}
          />
        </div>
      )}

      {/* Period badges (H1, H2, Q1–Q4) – positioned to align with the winner outline corners */}
      {wonPeriodBadges.map(({ label, position }, i) => {
        const pos = position >= 0 && position <= 3 ? position : 0;
        return (
          <div
            key={`${label}-${pos}-${i}`}
            className={`absolute rounded-[4px] bg-[#53d337] border border-[#53d337] text-black font-bold leading-none z-10 winner-badge-in ${WINNER_BADGE_POSITION[pos as BadgePosition]}`}
            style={{
              fontSize: WINNER_BADGE_FONT_SIZE,
              padding: '1px 3px',
              animationDelay: `${outlineRevealDelay + outlineDurationS}s`,
              opacity: 0,
            }}
          >
            {label}
          </div>
        );
      })}

      {showResults && square.percentage !== undefined ? (
        // Results mode: rounded blank SVG background with percentage overlay; heatmap tiers grade opacity (highest % = brightest)
        <div className="relative w-full h-full flex items-center justify-center min-w-0 min-h-0 overflow-hidden">
          <img
            src={isSelected ? '/Rounded-white-blank.svg' : '/Rounded-gray-blank.svg'}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
          />
          <span
            className={`relative font-medium shrink-0 min-w-0 ${wonPeriodBadges.length > 0 ? 'z-30' : 'z-10'} ${isSelected ? 'text-black' : 'text-white'}`}
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: SQUARE_PERCENT_FONT_SIZE,
              lineHeight: 1.1,
              // Tier-based opacity on percentage text only (unselected squares); selected stays full opacity for readability
              opacity: isSelected
                ? 1
                : square.percentageTier
                  ? (PERCENTAGE_TIER_OPACITY[square.percentageTier] ?? 1)
                  : 1,
            }}
          >
            {(square.percentage * 100).toFixed(1)}%
          </span>
        </div>
      ) : (
        // Normal mode: rounded square asset. Cells are aspect-square at every
        // breakpoint, so the composite (frame + crown) renders as drawn.
        <img
          src={isSelected ? '/white-rounded-selected.svg' : '/gray-rounded.svg'}
          alt=""
          className="w-full h-full object-contain"
        />
      )}
    </button>
  );
}
